/**
 * The product's write model: the screens' own objects, in the shape the API accepts.
 *
 * `from-api.ts` is this mapping in the other direction, and the two are tested as a round trip —
 * a draft written through here and read back through there must come out identical, or the
 * designer silently loses work the moment it is saved.
 *
 * The split is the same one `from-api.ts` documents: everything the backend validates or enforces
 * goes to its OWN field (key, data type, required, visibility, holder role, publication policy,
 * duplicate policy, permissions, layer kind, bound field, asset digest, font family, z-index), and
 * the prototype's presentation model rides inside the contract's free objects — `layout`,
 * `placement` and `style` — which the publish gate snapshots verbatim into the immutable release.
 */

import type { UpdateDraftBody } from '../../api/internal';
import type { FieldDefinition, PublicationPolicy } from '../../api/types';
import { buildFontReferences } from '../lib/publish-gate';
import type { EditorLayout } from '../screens/editor/contract';
import { V07_LAYOUT_KEY, type V07TemplateModel } from './from-api';
import { layerToApi } from './layer-geometry';
import type { DemoDraft, DemoFieldDefinition } from './types';

/**
 * The two backend columns a four-valued prototype visibility maps onto.
 *
 * `private` is private. Every `public_…` value is public: it is drawn on an artwork anyone with the
 * link can see, and calling that «not publicly visible» was a contradiction the publish gate refused
 * outright — `private_field_on_public_layer` for every layer bound to a `public_render` field, which
 * is every automatic field the wizard creates (title, date, code, QR).
 *
 * The prototype's own distinction — `public_render` is drawn but not repeated in the public page's
 * claim list — has no column of its own and never did: it survives in `fieldMeta`, where
 * `fieldFromApi` reads it back, and the prototype's screens go on honouring it. What it is not is a
 * claim that the value is hidden, because it is not. Recorded as a gap in `V07-API-GAP-MATRIX.md`
 * 1.7: the backend's public read model has no «drawn but unlisted» state, so a `public_render` field
 * that carries a claim value is listed by the API's own projection.
 */
export function fieldVisibilityToApi(v: DemoFieldDefinition['visibility']): {
  visibility: FieldDefinition['visibility'];
  publicVisible: boolean;
} {
  if (v === 'private') return { visibility: 'private', publicVisible: false };
  return { visibility: 'public', publicVisible: true };
}

/**
 * The three prototype data types the backend's enum has no member for.
 *
 * `field_data_type` is a closed PostgreSQL enum (migration 001: string, text, number, integer, date,
 * datetime, email, url, enum, credential_id) and the product offers three types that are not in it.
 * `verification_qr` is a `qr` and `credential_url` is a `credential_url`: both hold the record's own
 * canonical address — the QR is that address drawn as a square — so `url` is the member that
 * describes them. «Evet / Hayır» is a `boolean`, which the enum has no member for at all; `string` is
 * the widest member that can hold it losslessly.
 *
 * The prototype's own word is not lost: it rides in `fieldMeta`, where `fromApi` reads it back, so
 * the designer, the validator and the render plan all still see the type the author chose.
 *
 * Sent untranslated, the whole draft write came back 422 `invalid_value` (SQLSTATE 22P02, an enum
 * with no such label). The wizard creates a template and then saves its design in a second request,
 * so EVERY template made through the wizard was stored with the backend's DEFAULT draft — no fields,
 * no layers, none of the seven steps' work — while the screen said the template could not be created
 * at all. The gap for `boolean` is recorded in `V07-API-GAP-MATRIX.md` 1.6: the backend validates
 * such a field as free text and the boolean rule is the product's own.
 */
const API_DATA_TYPE: Record<string, string> = {
  qr: 'url',
  credential_url: 'url',
  /*
   * «Evet / Hayır» is a two-option list, and the contract already has closed, server-validated
   * lists: `enum` + `enumOptions` (`domain/validation/field-value.ts`). Sending it as `string` made
   * the backend accept any sentence for a field the product presents as a yes/no — the type was
   * enforced nowhere. The two options are the exact values the product's own validator normalises
   * every accepted spelling to, so nothing about what an operator may type changes.
   */
  boolean: 'enum',
};

/** The options a type carries of its own accord. A yes/no has exactly two, and they are its own. */
const BUILT_IN_ENUM_OPTIONS: Record<string, string[]> = { boolean: ['true', 'false'] };

export const dataTypeToApi = (t: string): string => API_DATA_TYPE[t] ?? t;

/**
 * Which kind of address a `url` field holds — required by the schema, not optional.
 *
 * `field_definitions_url_policy_required` (migration 004) refuses a `url` field with no policy, so
 * the two types that BECOME `url` have to name theirs. Both are the record's own public address, and
 * `public_credential` is the member that says exactly that; a URL an author defines themselves is an
 * address of somebody else's, which is `external`.
 */
const URL_POLICY: Record<string, string> = {
  qr: 'public_credential',
  credential_url: 'public_credential',
  url: 'external',
};

export const urlPolicyToApi = (t: string): string | null => URL_POLICY[t] ?? null;

/** The backend's own half of a field definition. */
export function fieldToApi(f: DemoFieldDefinition, sortOrder: number): Record<string, unknown> {
  const { visibility, publicVisible } = fieldVisibilityToApi(f.visibility);
  return {
    key: f.key,
    label: f.labels?.tr ?? f.labels?.en ?? f.key,
    dataType: dataTypeToApi(f.dataType),
    ...(urlPolicyToApi(f.dataType) ? { urlPolicy: urlPolicyToApi(f.dataType) } : {}),
    required: f.required,
    visibility,
    publicVisible,
    holderRole: f.role ?? null,
    /* The options travel in the backend's own column; `validation.enum` is the prototype's name
       for the same list and is kept out of the free-form block so the two cannot drift. */
    ...(BUILT_IN_ENUM_OPTIONS[f.dataType]
      ? { enumOptions: BUILT_IN_ENUM_OPTIONS[f.dataType] }
      : Array.isArray((f.validation as { enum?: unknown })?.enum)
        ? { enumOptions: ((f.validation as { enum: unknown[] }).enum ?? []).map(String) }
        : {}),
    validation: f.validation ?? {},
    defaultValue: typeof f.defaultValue === 'string' ? f.defaultValue : null,
    sortOrder,
  };
}

/** The half the backend has no column for; stored under the field's key in the layout block. */
export function fieldMetaToApi(f: DemoFieldDefinition): Record<string, unknown> {
  return {
    id: f.id,
    /* Kept only where the wire type is not the prototype's own, so nothing else grows a field. */
    ...(dataTypeToApi(f.dataType) === f.dataType ? {} : { dataType: f.dataType }),
    labels: f.labels ?? {},
    importable: f.importable,
    visibility: f.visibility,
    normalization: f.normalization ?? [],
    sample: f.sample ?? '',
    auto: f.auto,
    custom: f.custom,
  };
}

/** The prototype's template model, ready to sit inside the backend's free `layout`. */
export function templateModelToLayout(
  draft: DemoDraft,
  existingLayout: Record<string, unknown> = {},
): Record<string, unknown> {
  const model: V07TemplateModel = {
    credentialType: draft.credentialType,
    customTypeLabel: draft.customTypeLabel,
    subjectType: draft.subjectType,
    holderField: draft.holderField,
    defaultLanguage: draft.defaultLanguage,
    localizedMetadata: draft.localizedMetadata as unknown as Record<string, Record<string, string>>,
    validityPolicy: draft.validityPolicy as unknown as Record<string, unknown>,
    emailDefaults: draft.emailDefaults,
    interoperability: draft.interoperability,
    canvas: draft.canvas as unknown as Record<string, unknown>,
    background: draft.background,
    localizedBackground: draft.localizedBackground,
    sealedLanguages: draft.sealedLanguages,
    safeAreas: draft.safeAreas,
    fieldMeta: Object.fromEntries(draft.fieldDefinitions.map((f) => [f.key, fieldMetaToApi(f)])),
    /* Null rather than absent when it was cleared, so the write really removes it. The server
       normalises and re-validates it before it is stored; this only carries it. */
    linkedInIssuer: draft.linkedInIssuer ?? null,
  };
  return {
    ...existingLayout,
    /*
     * The backend's own two entries stay at the top level and stay in the units it expects: its
     * renderer reads `canvas` for the output size. The prototype's canvas carries more (aspect
     * ratio, orientation, unit, output scale) and lives inside the namespaced block.
     */
    canvas: { width: Number(draft.canvas.width), height: Number(draft.canvas.height) },
    output: {
      width: Number(draft.canvas.width),
      height: Number(draft.canvas.height),
      format: 'png',
      scale: Number(draft.canvas.outputScale ?? 1),
      colorProfile: 'sRGB',
    },
    renderProfile: draft.renderProfile,
    /*
     * The artwork the whole drawing sits on — canonical, beside `canvas` and `output`.
     *
     * The prototype's own background object (fit mode, focal point, opacity, crop) stays in the
     * namespaced block; what the backend needs is the pair it can verify and load, and without it
     * every document was drawn on nothing while the editor went on showing the chosen picture.
     */
    ...backgroundToApi(draft),
    /*
     * The artwork each sealed language draws on, in the same canonical pair the backend verifies.
     *
     * Beside `background` rather than inside it: the issuing path loads every backdrop a layout
     * names before it draws, and it reads them from here. A language absent from this object is a
     * language this release cannot draw, which is what the publish gate refuses — nothing here
     * substitutes one language's picture for another's.
     */
    ...localizedBackgroundToApi(draft),
    /* Which languages a document is sealed in. Absent or one entry is today's behaviour. */
    sealedLanguages: draft.sealedLanguages,
    [V07_LAYOUT_KEY]: model,
  };
}

/** The canonical `layout.localizedBackground`: one verifiable pair per language that has one. */
export function localizedBackgroundToApi(draft: DemoDraft): {
  localizedBackground?: Record<string, { assetId: string; sha256: string; style?: Record<string, unknown> }>;
} {
  const out: Record<string, { assetId: string; sha256: string; style?: Record<string, unknown> }> = {};
  /*
   * The PLACEMENT is the release's own and is deliberately shared.
   *
   * What differs between two languages is which picture carries which words, not where the picture
   * sits: an author who chose «Sığdır» and moved the centre chose it for this certificate, not for
   * Turkish. So each language contributes its asset and its digest, and the fit, focal point, crop,
   * scale, opacity and rotation come from `background` — one geometry, two pictures. There is no
   * per-language geometry in this product and none is invented here.
   */
  const shared = canonicalBackground(draft.background)?.style;
  for (const locale of ['tr', 'en'] as const) {
    const ref = canonicalBackground(draft.localizedBackground?.[locale] ?? null);
    if (ref) out[locale] = { assetId: ref.assetId, sha256: ref.sha256, ...(shared ? { style: shared } : {}) };
  }
  return Object.keys(out).length > 0 ? { localizedBackground: out } : {};
}

/** The canonical `layout.background`: the asset the drawing sits on, and the digest it must have. */
export function backgroundToApi(draft: DemoDraft): {
  background?: { assetId: string; sha256: string; style?: Record<string, unknown> };
} {
  const ref = canonicalBackground(draft.background);
  return ref ? { background: ref } : {};
}

/**
 * One of the editor's background objects, in the pair the backend can verify and load.
 *
 * Extracted from `backgroundToApi` unchanged so a language's artwork is converted by exactly the
 * same rule as the release's own: two pictures that differ only in which language they carry must
 * not differ in how their placement reaches the renderer.
 */
export function canonicalBackground(
  value: Record<string, unknown> | null | undefined,
): { assetId: string; sha256: string; style?: Record<string, unknown> } | null {
  const bg = value as Record<string, unknown> | null;
  const ref = (bg as { assetRef?: { assetId?: unknown; sha256?: unknown } } | null)?.assetRef;
  const assetId = ref?.assetId;
  const sha256 = ref?.sha256;
  if (typeof assetId !== 'string' || typeof sha256 !== 'string' || !assetId || !sha256) return null;
  /*
   * The «Yerleşim», centre, crop, opacity and rotation the author set on the artwork.
   *
   * The renderer always drew the background `xMidYMid slice` — SVG's `cover` — so an author who
   * chose «Sığdır» or «Kırp», or moved the centre, got a differently cropped certificate than the
   * one in front of them. The editor's own background object keeps all of it; this is the canonical
   * copy the drawing reads, and its absence still means `cover`.
   */
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const style: Record<string, unknown> = {};
  if (typeof bg?.fitMode === 'string' && bg.fitMode) style.imageFit = bg.fitMode;
  if (num(bg?.x) !== undefined) style.x = bg?.x;
  if (num(bg?.y) !== undefined) style.y = bg?.y;
  if (num(bg?.opacity) !== undefined && (bg?.opacity as number) < 1) style.opacity = bg?.opacity;
  if (num(bg?.rotation) !== undefined && bg?.rotation !== 0) style.rotation = bg?.rotation;
  if (bg?.crop && typeof bg.crop === 'object') style.crop = { ...(bg.crop as object) };
  /* «Ölçek» and «Odak» — the two the placement solver reads and `preserveAspectRatio` cannot say. */
  if (num(bg?.scale) !== undefined) style.scale = bg?.scale;
  if (bg?.focal && typeof bg.focal === 'object') style.focal = { ...(bg.focal as object) };
  return { assetId, sha256, ...(Object.keys(style).length > 0 ? { style } : {}) };
}

/** Permissions, in the backend's names. `allowLinkedInShare` is its `allowFeedShare`. */
export function permissionsToApi(draft: DemoDraft): Record<string, unknown> {
  return {
    allowDownload: draft.permissions.allowDownload,
    allowLinkedInProfile: draft.permissions.allowLinkedInProfile,
    allowFeedShare: draft.permissions.allowLinkedInShare,
  };
}

/**
 * One draft save.
 *
 * Fields and layers always travel together: `updateDraft` clears the layers when it is given
 * fields without them, and a designer that saved half its drawing would be a data-loss bug rather
 * than a validation one.
 */
export { layerFromApi, layerToApi } from './layer-geometry';

export function draftToApi(
  draft: DemoDraft,
  opts: { canonicalTitle: string; existingLayout?: Record<string, unknown> },
): UpdateDraftBody {
  return {
    settings: {
      canonicalTitle: opts.canonicalTitle,
      publicationPolicy: draft.publicationPolicy as PublicationPolicy,
      layout: templateModelToLayout(draft, opts.existingLayout ?? {}),
      duplicatePolicy: draft.duplicatePolicy as unknown as Record<string, unknown>,
      permissions: permissionsToApi(draft),
      /*
       * Which font asset each family on the drawing is bound to — the draft's own column.
       *
       * It was never sent, so the stored draft always claimed no font references at all, and the
       * server's publish gate refuses a text layer whose family has none: «font ailesi referanssız»
       * for every single layer. No release could be cut through the product at all — the only
       * published releases in this system came from a seed that wrote them directly.
       *
       * `buildFontReferences` is the client's own binder (`m02-mockdb.js:322`), already used by the
       * publish gate's checklist; using it here is what makes the two agree. It answers from the
       * render library, which the product fills from `GET /fonts` — so a family with a stored face
       * comes out `asset_verified` with that face's id and digest, and a family with none says so
       * rather than inventing one. Nothing is fabricated: the server verifies every id and digest
       * against its own tables before it freezes the release.
       */
      fontReferences: buildFontReferences(draft as unknown as EditorLayout),
    },
    fields: draft.fieldDefinitions.map((f, i) => fieldToApi(f, i)),
    layers: draft.layers.map((l) => layerToApi(l, draft.canvas as never)),
  };
}
